import { hasMeetingLocation } from "../../utils/meetingLocationSelection";
import MeetingLocationPicker from "../../components/meetingLocation/MeetingLocationPicker";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowLeft,
  Heart,
  Calendar,
  FileText,
  Bone,
  X,
  Loader2,
  ChevronDown,
  MapPin,
} from "lucide-react";
import "./CurrentBookingsPage.css";
import { format } from "date-fns";
import apiClient from "../../api/apiClient";
import dogSettingsIcon from "../../assets/progressIcon6.png";
import { useGlobalAlert } from "../../context/AlertContext";
import TopNavBar from "../../components/TopNavBar";
import morningIcon from "../../assets/availability1.png";
import afternoonIcon from "../../assets/availability2.png";
import eveningIcon from "../../assets/availability3.png";
import weekedaysIcon from "../../assets/availability4.png";
import weekenedsIcon from "../../assets/availability5.png";
import vacationIcon from "../../assets/availability6.png";
import dayjs from "dayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { Box } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";
import appIcon from "../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../components/MobileTopBar";

const userDetailsCache = new Map();

const fetchUserDetailsCached = (userId) => {
  if (!userId) return null;
  if (userDetailsCache.has(userId)) {
    return userDetailsCache.get(userId);
  }
  const promise = apiClient
    .get(`/user/details/${userId}`)
    .then((res) => res.data?.data || null)
    .catch((err) => {
      console.error("Error fetching user details:", err);
      return null;
    });

  userDetailsCache.set(userId, promise);
  return promise;
};

const getFirstName = (fullName) => {
  if (!fullName || typeof fullName !== "string") return fullName;
  return fullName.trim().split(" ")[0];
};

const getDynamicDateLabel = (date) => {
  if (!date) return "FLEXIBLE";
  const target = dayjs(date);
  const today = dayjs();

  if (target.isSame(today, "day")) return "Today";
  if (target.isSame(today.add(1, "day"), "day")) return "Tomorrow";

  return "DATE";
};

const getDynamicTimeLabel = (date) => {
  if (!date) return "TIME";
  const hour = dayjs(date).hour();

  if (hour >= 0 && hour < 12) return "MORNING";
  if (hour >= 12 && hour < 17) return "AFTERNOON";

  return "EVENING";
};

const calculateUIStatus = (booking, currentUserId) => {
  if (!booking || !currentUserId) return "Pending";

  const isReq = String(booking.requester_user_id) === String(currentUserId);
  const myCompletion = isReq
    ? booking.requester_is_completed
    : booking.walker_is_completed;

  const apiStatus = (booking.status || "").toLowerCase();

  let uiStatus = "Pending";
  let isPastDay = false;
  let isToday = false;

  if (booking.date) {
    const bookingDate = dayjs(booking.date);

    if (bookingDate.isValid()) {
      const now = dayjs();
      isPastDay = now.isAfter(bookingDate.endOf("day"));
      isToday = now.isSame(bookingDate, "day");
    }
  }

  if (booking.is_cancelled) {
    uiStatus = "Cancelled";
  } else if (myCompletion) {
    uiStatus = "Completed";
  } else if (isToday && apiStatus === "approved") {
    uiStatus = "Today";
  } else if (apiStatus === "approved") {
    uiStatus = "Accepted";
  } else if (apiStatus === "pending") {
    uiStatus = "Pending";
  } else if (isPastDay) {
    uiStatus = "Expired";
  } else {
    uiStatus = apiStatus
      ? apiStatus.charAt(0).toUpperCase() + apiStatus.slice(1)
      : "Pending";
  }

  return uiStatus;
};

const getStatusClass = (status) => {
  const normalized = status?.toLowerCase();
  if (normalized === "today" || normalized === "accepted") return "green";
  if (normalized === "cancelled") return "red";
  return normalized;
};

const CurrentBookingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { triggerAlert } = useGlobalAlert();
  const { profile } = useSelector((state) => state.user);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [user, setUser] = useState(null);
  const [partner, setPartner] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [viewMode, setViewMode] = useState("default");
  const [actionLoading, setActionLoading] = useState(null);
  const [milestone, setMilestone] = useState(0);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [treatDog, setTreatDog] = useState(null);
  const [showBookWalkModal, setShowBookWalkModal] = useState(false);
  const [showMeetingPointModal, setShowMeetingPointModal] = useState(false);
  const [showWalkConfirmModal, setShowWalkConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [walkTab, setWalkTab] = useState("calendar");
  const [value, setValue] = useState(dayjs());
  const [selectedFlexibleOptions, setSelectedFlexibleOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [walkMessage, setWalkMessage] = useState("");
  const [selectedMeetingAddress, setSelectedMeetingAddress] = useState("");
  const [selectedMeetingLocation, setSelectedMeetingLocation] = useState(null);
  const [userLocation] = useState({
    lat: 40.7648,
    lng: -73.9808,
  });

  const timeDropdownRef = useRef(null);

  const TIME_SLOTS = useMemo(() => {
    const interval = 30;
    const slots = [];
    const now = dayjs();
    const isToday = value && value.isSame(now, "day");

    const currentMinute = now.minute();
    const addMinutes = interval - (currentMinute % interval);
    const nextAvailableTime = now
      .clone()
      .add(addMinutes, "minute")
      .second(0)
      .millisecond(0);

    for (let h = 6; h <= 21; h++) {
      for (let m = 0; m < 60; m += interval) {
        if (h === 21 && m > 0) break;

        const slotTime = (value || now)
          .clone()
          .hour(h)
          .minute(m)
          .second(0)
          .millisecond(0);

        if (isToday) {
          if (
            slotTime.isSame(nextAvailableTime) ||
            slotTime.isAfter(nextAvailableTime)
          ) {
            slots.push(slotTime.format("hh:mm A"));
          }
        } else {
          slots.push(slotTime.format("hh:mm A"));
        }
      }
    }

    return slots;
  }, [value]);

  const toggleFlexibleOption = (option) => {
    if (selectedFlexibleOptions.includes(option)) {
      setSelectedFlexibleOptions((prev) => prev.filter((o) => o !== option));
    } else {
      setSelectedFlexibleOptions((prev) => [...prev, option]);
    }
  };

  const selectedBooking = useMemo(
    () => bookings.find((b) => b.id === selectedId),
    [bookings, selectedId],
  );

  const handleRescheduleClick = () => {
    if (!selectedBooking) return;
    setTreatDog(selectedBooking);
    setSelectedMeetingAddress(selectedBooking.meeting_location || "");
    setShowBookWalkModal(true);

    if (selectedBooking.date) {
      const d = dayjs(selectedBooking.date);
      setValue(d);
      setWalkTab("calendar");
    } else {
      setSelectedFlexibleOptions(selectedBooking.availability || []);
      setWalkTab("flexible");
    }
  };

  const handleFlexibleSubmit = () => {
    if (selectedFlexibleOptions.length === 0) return;
    setSelectedDate(null);
    setSelectedTime(null);
    openMeetingPoint();
  };

  const openMeetingPoint = () => {
    setShowBookWalkModal(false);
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(true);
  };

  const openWalkConfirm = () => {
    if (!hasMeetingLocation(selectedMeetingAddress)) {
      triggerAlert("Please select a meeting location.", "error");
      return;
    }
    setShowMeetingPointModal(false);
    setShowWalkConfirmModal(true);
  };

  const backToBookWalk = () => {
    setShowMeetingPointModal(false);
    setShowBookWalkModal(true);
  };

  const backToMeetingPoint = () => {
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(true);
  };

  const closeAllModalsFully = () => {
    setShowBookWalkModal(false);
    setShowMeetingPointModal(false);
    setShowWalkConfirmModal(false);
    setShowSuccessModal(false);
    setShowCompleteModal(false);
    setTreatDog(null);
    setSelectedMeetingAddress("");
    setSelectedMeetingLocation(null);
    setValue(dayjs());
    setSelectedDate(null);
    setSelectedTime(null);
    setWalkMessage("");
  };

  const handleRescheduleSubmit = async () => {
    if (!hasMeetingLocation(selectedMeetingAddress)) {
      triggerAlert("Please select a meeting location.", "error");
      return;
    }
    if (!selectedBooking) return;
    setIsRescheduling(true);

    try {
      let payload = {};

      if (walkTab === "calendar") {
        payload = {
          date: selectedDate ? selectedDate.toISOString() : null,
          availability: [],
          meeting_location: selectedMeetingAddress.trim(),
          sender_offer_message: walkMessage,
        };
      } else {
        payload = {
          date: null,
          availability: selectedFlexibleOptions,
          meeting_location: selectedMeetingAddress.trim(),
          sender_offer_message: walkMessage,
        };
      }

      const res = await apiClient.put(
        `/bookwalks/update-walk-byId/${selectedBooking.id}`,
        payload,
      );

      if (res.data?.status) {
        triggerAlert("Booking rescheduled successfully!", "success");
        closeAllModalsFully();
        await fetchAllBookings();
      }
    } catch (err) {
      console.error("Reschedule Error:", err);
      triggerAlert("Failed to reschedule walk.", "error");
    } finally {
      setIsRescheduling(false);
    }
  };

  const userRole = useMemo(() => {
    return profile?.user_dog_status === 1 ? "pup_parent" : "pup_pals";
  }, [profile?.user_dog_status]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      const data = await fetchUserDetailsCached(profile?.id);
      if (data) setUser(data);
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  const isMyProfileTeamPaws =
    user?.user_dog_status === 1 ||
    user?.team_paws === 1 ||
    user?.dogs?.[0]?.team_paws === 1;

  const myDogImageRaw =
    user?.dogs?.[0]?.dog_images?.[0] || user?.dog_images?.[0];
  const myUserImageRaw =
    user?.user_profile_image?.[0] ||
    user?.profile_image?.[0] ||
    user?.dogs?.[0]?.profile_image?.[0];

  let resolvedMyImage;
  if (isMyProfileTeamPaws) {
    if (myDogImageRaw && myDogImageRaw.trim() !== "") {
      resolvedMyImage = myDogImageRaw;
    } else if (myUserImageRaw && myUserImageRaw.trim() !== "") {
      resolvedMyImage = myUserImageRaw;
    } else {
      resolvedMyImage = dogPlaceholder;
    }
  } else {
    if (myUserImageRaw && myUserImageRaw.trim() !== "") {
      resolvedMyImage = myUserImageRaw;
    } else {
      resolvedMyImage = userPlaceholder;
    }
  }

  const myDogName = getFirstName(user?.dogs?.[0]?.dog_name || "My Dog");
  const myDogImage = resolvedMyImage;
  const userDogStatus = user?.user_dog_status || 0;
  const myName = getFirstName(user?.user_name);
  const myImage = myUserImageRaw || userPlaceholder;

  const fetchAllBookings = React.useCallback(async () => {
    if (!profile?.id) return;
    try {
      const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const fetchRequester = apiClient
        .get(
          `/bookwalks/get-requester-by-userId/${profile.id}?timezone=${encodeURIComponent(deviceTimezone)}`,
        )
        .catch(() => ({ data: { data: [] } }));

      const fetchWalker = apiClient
        .get(
          `/bookwalks/get-walks-by-walker_userId/${profile.id}?timezone=${encodeURIComponent(deviceTimezone)}`,
        )
        .catch(() => ({ data: { data: [] } }));

      const [reqRes, walkRes] = await Promise.all([
        fetchRequester,
        fetchWalker,
      ]);

      const transformData = async (data, isRequesterRole) => {
        if (!data) return [];
        return Promise.all(
          data.map(async (item) => {
            let formattedDate = "Flexible Schedule";
            let shortDate = "Flexible";
            let sortDate = new Date(0);

            let hasValidDate = false;

            if (item.date && String(item.date).trim() !== "") {
              const localDateString = item.date
                .replace("Z", "")
                .replace(".000", "");
              const bookingDate = new Date(localDateString);
              if (!isNaN(bookingDate)) {
                formattedDate = item.formatted_date;
                shortDate = item.short_date;
                sortDate = bookingDate;
                hasValidDate = true;
              }
            }

            if (
              !hasValidDate &&
              item.availability &&
              item.availability.length > 0
            ) {
              formattedDate = `Flexible: ${item.availability.join(", ")}`;
              shortDate = "Flexible";
            }

            const pId = isRequesterRole
              ? item.walker_user_id
              : item.requester_user_id;

            const pDetails = await fetchUserDetailsCached(pId);

            const rawHumanName = isRequesterRole
              ? item.walker_user_name
              : item.requester_user_name;
            const pHumanAvatar = isRequesterRole
              ? item.walker_user_profile_image?.[0]
              : item.requester_user_profile_image?.[0];

            const pFirstName = getFirstName(rawHumanName);

            const pIsTeamPaws =
              pDetails?.user_dog_status === 1 ||
              pDetails?.team_paws === 1 ||
              pDetails?.dogs?.[0]?.team_paws === 1;

            const pDogImg =
              pDetails?.dogs?.[0]?.dog_images?.[0] || pDetails?.dog_images?.[0];
            const pUserImg =
              pHumanAvatar ||
              pDetails?.user_profile_image?.[0] ||
              pDetails?.profile_image?.[0] ||
              pDetails?.dogs?.[0]?.profile_image?.[0];

            let displayAvatar;
            if (pIsTeamPaws) {
              if (pDogImg && pDogImg.trim() !== "") displayAvatar = pDogImg;
              else if (pUserImg && pUserImg.trim() !== "")
                displayAvatar = pUserImg;
              else displayAvatar = dogPlaceholder;
            } else {
              if (pUserImg && pUserImg.trim() !== "") displayAvatar = pUserImg;
              else displayAvatar = userPlaceholder;
            }

            let displayName = pFirstName;
            if (pIsTeamPaws && pDetails?.dogs?.[0]?.dog_name) {
              displayName = getFirstName(pDetails.dogs[0].dog_name);
            }

            const enrichedItem = {
              ...item,
              api_status: item.status,
              partnerName: pFirstName,
              partnerAvatar: pHumanAvatar,
              displayPartnerName: displayName,
              displayPartnerAvatar: displayAvatar,
              displayTitle: `Walk with ${displayName}`,
              formatted_date: formattedDate,
              short_date: shortDate,
              sort_key: sortDate,
            };

            return {
              ...enrichedItem,
              status: calculateUIStatus(enrichedItem, profile.id),
            };
          }),
        );
      };

      const reqTransformed = await transformData(reqRes.data?.data, true);
      const walkTransformed = await transformData(walkRes.data?.data, false);

      const combined = [...reqTransformed, ...walkTransformed].sort(
        (a, b) => b.sort_key - a.sort_key,
      );

      setBookings(combined);
    } catch (err) {
      console.error("Error fetching bookings:", err);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    setLoading(true);
    fetchAllBookings();
  }, [fetchAllBookings]);

  useEffect(() => {
    if (bookings.length > 0 && !selectedId) {
      const queryParams = new URLSearchParams(location.search);
      const urlBookingId = queryParams.get("bookingId");

      const targetId = urlBookingId
        ? parseInt(urlBookingId, 10)
        : location.state?.returnId;

      if (targetId) {
        const bookingExists = bookings.some((b) => b.id === targetId);

        if (bookingExists) {
          setSelectedId(targetId);
          setShouldScroll(true);

          setShowMobileDetail(true);

          if (urlBookingId) {
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname,
            );
          } else {
            window.history.replaceState({}, document.title);
          }
          return;
        }
      }

      setSelectedId(bookings[0].id);
    }
  }, [bookings, selectedId, location.search, location.state?.returnId]);

  useEffect(() => {
    if (shouldScroll && location.state?.returnId && bookings.length > 0) {
      const timer = setTimeout(() => {
        const element = document.getElementById(
          `booking-card-${location.state?.returnId}`,
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          setShouldScroll(false);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [shouldScroll, location.state?.returnId, bookings]);

  const isBookingPast = useMemo(() => {
    if (!selectedBooking?.date) return false;
    return new Date() > new Date(selectedBooking.date);
  }, [selectedBooking]);

  const isRequester = useMemo(() => {
    if (!selectedBooking || !profile?.id) return false;
    return String(selectedBooking.requester_user_id) === String(profile.id);
  }, [selectedBooking, profile?.id]);

  const isFirstWalk = useMemo(() => {
    if (!selectedBooking || bookings.length === 0) return true;

    const partnerId = isRequester
      ? selectedBooking.walker_user_id
      : selectedBooking.requester_user_id;

    const walksWithPartner = bookings.filter(
      (b) =>
        b.walker_user_id === partnerId || b.requester_user_id === partnerId,
    );

    const sortedWalks = [...walksWithPartner].sort((a, b) => {
      const timeA = a.sort_key?.getTime() || 0;
      const timeB = b.sort_key?.getTime() || 0;
      if (timeA === timeB) return a.id - b.id;
      return timeA - timeB;
    });

    return sortedWalks.length > 0
      ? sortedWalks[0].id === selectedBooking.id
      : true;
  }, [bookings, selectedBooking, isRequester]);

  const hasUserActed = useMemo(() => {
    if (!selectedBooking) return false;
    return (
      selectedBooking.is_cancelled ||
      (isRequester
        ? selectedBooking.requester_is_completed
        : selectedBooking.walker_is_completed)
    );
  }, [selectedBooking, isRequester]);

  useEffect(() => {
    if (!selectedBooking) return;
    const partnerId = isRequester
      ? selectedBooking.walker_user_id
      : selectedBooking.requester_user_id;

    const fetchPartnerDetails = async () => {
      const data = await fetchUserDetailsCached(partnerId);
      if (data) setPartner(data);
    };
    if (partnerId) fetchPartnerDetails();
  }, [selectedBooking?.id, isRequester]);

  const partnerHasDog =
    partner?.user_dog_status === 1 || partner?.team_paws === 1;
  const partnerDogName = getFirstName(partner?.dogs?.[0]?.dog_name || "Dog");

  useEffect(() => {
    if (!selectedBooking || !profile?.id) return;

    const fetchMilestones = async () => {
      const { walker_user_id, requester_user_id } = selectedBooking;
      if (!walker_user_id || !requester_user_id) return;

      try {
        const payload = {
          requester_user_id: isRequester ? profile.id : requester_user_id,
          walker_user_id: isRequester ? walker_user_id : profile.id,
        };

        const res = await apiClient.post(
          "/milestone/get-requester-and-walker-milestones",
          payload,
        );

        if (res.data?.status && res.data.data) {
          const { requester, walker } = res.data.data;
          const partnerData =
            requester.user_id !== profile.id ? requester : walker;
          setMilestone(partnerData.milestone || 0);
        }
      } catch (err) {
        console.error("Error fetching milestones:", err);
      }
    };

    fetchMilestones();
  }, [selectedBooking?.id, isRequester, profile?.id]);

  const updateLocalBookingStatus = (id, updatedData) => {
    setBookings((prev) =>
      prev.map((b) => {
        if (b.id === id) {
          const newBooking = { ...b, ...updatedData };
          newBooking.status = calculateUIStatus(newBooking, profile?.id);
          return newBooking;
        }
        return b;
      }),
    );
  };

  const isLoggedUserRated = useMemo(() => {
    if (!selectedBooking || !profile?.id) return false;

    const loggedUserId = String(profile.id);
    const requesterId = String(selectedBooking.requester_user_id);
    const walkerId = String(selectedBooking.walker_user_id);
    if (loggedUserId === requesterId) {
      return selectedBooking.requester_is_rated === true;
    } else if (loggedUserId === walkerId) {
      return selectedBooking.walker_is_rated === true;
    }

    return false;
  }, [selectedBooking, profile?.id]);

  const handleAction = async (action) => {
    if (!selectedBooking) return;

    if (action === "Complete" && !isBookingPast) {
      return triggerAlert(
        "You can only mark this walk as completed after the scheduled booking time.",
        "error",
      );
    }

    setActionLoading(action);

    try {
      if (action === "Complete") {
        const payload = isRequester
          ? { requester_is_completed: true }
          : { walker_is_completed: true };
        const res = await apiClient.put(
          `/bookwalks/update-walk-byId/${selectedBooking.id}`,
          payload,
        );
        if (res.data?.status) {
          updateLocalBookingStatus(selectedBooking.id, payload);
          setShowCompleteModal(true);
        }
      } else if (action === "Cancel") {
        const payload = { is_cancelled: true };
        const res = await apiClient.put(
          `/bookwalks/update-walk-byId/${selectedBooking.id}`,
          payload,
        );
        if (res.data?.status) {
          updateLocalBookingStatus(selectedBooking.id, payload);
          setViewMode("default");
        }
      } else if (action === "NEXT") {
        setShowCompleteModal(false);
        setViewMode("completed");
        handleGoToPostWalkFeedbackPage();
      }
    } catch (err) {
      console.error(`Error during ${action}:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleGoToMessagesPage = () => {
    navigate("/messages", {
      state: {
        partnerId: isRequester
          ? selectedBooking.walker_user_id
          : selectedBooking.requester_user_id,
      },
    });
  };

  const handleGoToPostWalkFeedbackPage = () => {
    if (!selectedBooking) return;
    navigate("/team-paws-post-walk-feedback", {
      state: {
        bookingId: selectedBooking.id,
        partnerId: isRequester
          ? selectedBooking.walker_user_id
          : selectedBooking.requester_user_id,
        partnerName: selectedBooking.partnerName,
        partnerAvatar: selectedBooking.partnerAvatar,
        isRequester: isRequester,
      },
    });
  };

  const handleGoToAvailabilityPage2 = () => {
    if (!selectedBooking)
      return triggerAlert("Please select a booking.", "error");
    navigate("/team-social-availability", {
      state: {
        partnerId: isRequester
          ? selectedBooking.walker_user_id
          : selectedBooking.requester_user_id,
        partnerName: selectedBooking.partnerName,
        bookingId: selectedBooking.id,
      },
    });
  };

  const handleGoToPupCarePage = () => navigate("/walkies-and-food");

  const handleUnifiedAvailability = async () => {
    if (!selectedBooking)
      return triggerAlert("Please select a booking.", "error");

    const canProceed = milestone >= 2 && !selectedBooking.is_cancelled;

    if (!canProceed) {
      return triggerAlert(
        "Availability features are not available for this booking yet.",
        "error",
      );
    }

    const partnerId = isRequester
      ? selectedBooking.walker_user_id
      : selectedBooking.requester_user_id;

    navigate("/team-paws-availability", {
      state: {
        partnerId,
        myDogName,
        partnerName: selectedBooking.partnerName,
        bookingId: selectedBooking.id,
      },
    });
  };

  const handleAddToCalendar = () => {
    if (!selectedBooking || !selectedBooking.date) {
      triggerAlert(
        "Cannot add flexible or undated walks to calendar.",
        "error",
      );
      return;
    }

    try {
      const startDate = new Date(selectedBooking.date);

      if (isNaN(startDate.getTime())) {
        triggerAlert("Invalid date format.", "error");
        return;
      }

      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

      const formatForGoogle = (date) => {
        return date.toISOString().replace(/-|:|\.\d\d\d/g, "");
      };

      const startStr = formatForGoogle(startDate);
      const endStr = formatForGoogle(endDate);

      const title = encodeURIComponent(
        `Walk with ${selectedBooking.displayPartnerName || selectedBooking.partnerName}`,
      );
      const details = encodeURIComponent("Scheduled via Social Paws");
      const location = encodeURIComponent(
        selectedBooking.meeting_location || "",
      );

      const deviceTimezone = encodeURIComponent(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      );

      const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startStr}/${endStr}&details=${details}&location=${location}&ctz=${deviceTimezone}`;

      window.open(googleCalendarUrl, "_blank");
    } catch (err) {
      console.error("Calendar link generation error", err);
      triggerAlert("Failed to generate calendar link.", "error");
    }
  };

  const handleImgError = (e) => {
    e.target.src = dogPlaceholder;
  };

  const handleScrollToTime = () => {
    const isMobile =
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
      (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);

    if (!isMobile) return;

    setShowTimeDropdown(true);
    setTimeout(() => {
      timeDropdownRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  };

  const handleCalendarClick = (e) => {
    if (e.target.closest(".MuiPickersDay-root")) {
      handleScrollToTime();
    }
  };

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(157.14deg, #96f2de 2.76%, #58b8a3 96.76%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  const partnerImage =
    (isRequester
      ? selectedBooking?.walker_user_profile_image?.[0]
      : selectedBooking?.requester_user_profile_image?.[0]) || userPlaceholder;

  return (
    <div
      className="cb-page-container"
      style={{
        background:
          window.innerWidth <= 768
            ? "linear-gradient(139.27deg, #009fd9 23.13%, #005b7d 98.96%)"
            : "linear-gradient(157.14deg, #96f2de 2.76%, #58b8a3 96.76%)",
      }}
    >
      <div className="cb-custom-top-header cb-desktop-only">
        <div
          className="cb-custom-brand"
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "32px", height: "32px", objectFit: "contain" }}
          />
          <span>Social Paws</span>
        </div>
        <div className="header-icons-right">
          <TopNavBar theme="light" />
        </div>
      </div>

      <div className="cb-custom-top-header cb-mobile-only">
        <div className="cb-header-top-row">
          <MobileTopBar
            onBack={() => {
              if (isMobile && showMobileDetail) {
                setShowMobileDetail(false);
              } else {
                navigate("/dashboard-switcher");
              }
            }}
            backColor="#fff"
            logoColor="#fff"
          />
        </div>
        <div className="cb-header-bottom-row">
          <div className="header-icons-right">
            <TopNavBar theme="dark" />
          </div>
        </div>
      </div>

      <div
        className={`cb-header-title ${
          isMobile && showMobileDetail ? "mobile-hidden" : ""
        }`}
      >
        <h1 className="desktop-only">CURRENT BOOKINGS</h1>
        <h1 className="mobile-only">Current Bookings</h1>
        <p
          style={{
            fontSize: "0.9rem",
            marginTop: "5px",
            color: "inherit",
            visibility: window.innerWidth <= 768 ? "hidden" : "visible",
          }}
        >
          See all your current bookings here
        </p>
      </div>

      <div className="cb-content-wrapper">
        <div
          className={`cb-list-panel ${
            isMobile && showMobileDetail ? "mobile-hidden" : ""
          }`}
        >
          <h3 className="cb-list-header cb-desktop-only">All Bookings</h3>
          <div className="cb-list-scroll">
            {bookings.map((booking) => (
              <div
                key={booking.id}
                id={`booking-card-${booking.id}`}
                className={`cb-booking-card ${
                  selectedId === booking.id ? "active" : ""
                }`}
                onClick={() => {
                  setSelectedId(booking.id);
                  setViewMode("default");
                  setShowMobileDetail(true);
                }}
              >
                <div className="cb-card-avatar">
                  <img
                    src={booking.displayPartnerAvatar || userPlaceholder}
                    alt={booking.displayPartnerName}
                    onError={handleImgError}
                  />
                </div>
                <div className="cb-card-info">
                  <h4
                    className={`cb-card-title ${getStatusClass(booking.status)}`}
                  >
                    {booking.displayTitle}
                  </h4>
                  <div
                    className={`cb-card-meta ${getStatusClass(booking.status)}`}
                  >
                    {booking.date && String(booking.date).trim() !== "" ? (
                      <span>{booking.short_date}</span>
                    ) : booking.availability &&
                      booking.availability.length > 0 ? (
                      <span
                        className="cb-truncate-text"
                        title={booking.availability.join(", ")}
                      >
                        Flexible
                      </span>
                    ) : (
                      <span>{booking.short_date}</span>
                    )}
                    <span className="cb-meta-dot">•</span>
                    <span
                      className={`cb-status ${getStatusClass(booking.status)}`}
                    >
                      {booking.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedBooking && (
          <div
            className={`cb-detail-panel ${
              isMobile && !showMobileDetail ? "mobile-hidden" : ""
            }`}
          >
            <div className="cb-detail-header">
              <div className="cb-dh-left">
                <div className="cb-team-group">
                  <div className="cb-team-avatars">
                    <div className="cb-header-avatar" style={{ zIndex: 2 }}>
                      <img
                        src={partnerImage}
                        alt="Partner User"
                        onError={handleImgError}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const pId =
                            partner?.id ||
                            (isRequester
                              ? selectedBooking.walker_user_id
                              : selectedBooking.requester_user_id);
                          navigate(`/profile-details`, {
                            state: { activeView: "user", pId: pId },
                          });
                        }}
                      />
                    </div>
                    {partnerHasDog && (
                      <div
                        className="cb-header-avatar overlap"
                        style={{ zIndex: 1 }}
                      >
                        <img
                          src={
                            partner?.dogs?.[0]?.dog_images?.[0] ||
                            dogPlaceholder
                          }
                          alt="Partner Dog"
                          onError={handleImgError}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            const pId =
                              partner?.id ||
                              (isRequester
                                ? selectedBooking.walker_user_id
                                : selectedBooking.requester_user_id);
                            navigate(`/profile-details`, {
                              state: { activeView: "dog", pId: pId },
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="cb-team-names">
                    <div className="cb-name-item">
                      <Heart
                        size={18}
                        color="#727272"
                        strokeWidth={2.5}
                        className="msg-heart-icon"
                      />
                      <span className="cb-header-user-name">
                        {selectedBooking.partnerName}
                      </span>
                    </div>
                    {partnerHasDog && (
                      <div className="cb-name-item">
                        <Bone
                          size={18}
                          color="#000"
                          strokeWidth={2.5}
                          className="msg-bone-icon"
                        />
                        <span className="cb-header-user-name">
                          {partnerDogName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="cb-team-group">
                  <div className="cb-team-avatars">
                    <div className="cb-header-avatar" style={{ zIndex: 2 }}>
                      <img
                        src={myImage || userPlaceholder}
                        alt="Me"
                        onError={handleImgError}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          navigate(`/user-profile`, {
                            state: { activeView: "user" },
                          });
                        }}
                      />
                    </div>
                    {userDogStatus === 1 && (
                      <div
                        className="cb-header-avatar overlap"
                        style={{ zIndex: 1 }}
                      >
                        <img
                          src={myDogImage || dogPlaceholder}
                          alt="My Dog"
                          onError={handleImgError}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            navigate(`/user-profile`, {
                              state: { activeView: "dog" },
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="cb-team-names">
                    <div className="cb-name-item">
                      <Heart
                        size={18}
                        color="#727272"
                        strokeWidth={2.5}
                        className="msg-heart-icon"
                      />
                      <span className="cb-header-user-name">{myName}</span>
                    </div>
                    {userDogStatus === 1 && (
                      <div className="cb-name-item">
                        <Bone
                          size={18}
                          color="#000"
                          strokeWidth={2.5}
                          className="msg-bone-icon"
                        />
                        <span className="cb-header-user-name">{myDogName}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="cb-dh-right cb-desktop-only">
                {userRole === "pup_parent" && (
                  <div
                    className="msg-header-action"
                    onClick={handleUnifiedAvailability}
                    style={{
                      opacity: isCheckingAvailability ? 0.6 : 1,
                      pointerEvents: isCheckingAvailability ? "none" : "auto",
                    }}
                  >
                    {isCheckingAvailability ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Calendar size={18} />
                    )}
                    <span>
                      {myDogName}'s
                      <br />
                      Availability
                    </span>
                  </div>
                )}
                {userRole === "pup_pals" && (
                  <div
                    className="msg-header-action"
                    onClick={handleGoToAvailabilityPage2}
                  >
                    <Calendar size={18} />
                    <span>get Availability</span>
                  </div>
                )}
                <div
                  className="msg-header-action"
                  onClick={handleGoToPupCarePage}
                >
                  <FileText size={18} />
                  <span>
                    PupCare
                    <br />
                    Playbook
                  </span>
                </div>
              </div>
            </div>

            <div className="cb-detail-body">
              <div className="cb-detail-message">
                {selectedBooking.date &&
                  !selectedBooking.is_cancelled &&
                  ["approved", "approve", "confirm", "confirmed"].includes(
                    selectedBooking.api_status?.toLowerCase(),
                  ) && (
                    <button
                      className="cb-action-btn secondary"
                      style={{
                        border: "1px solid rgba(0, 159, 217, 0.5)",
                        color: "rgba(0, 159, 217, 1)",
                        width: "auto",
                        padding: "8px 20px",
                        marginBottom: "1.5rem",
                        fontSize: "14px",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        display: "inline-flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textTransform: "none",
                        fontWeight: "500",
                        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.05)",
                      }}
                      onClick={handleAddToCalendar}
                    >
                      Add to Google Calendar
                    </button>
                  )}

                <h2>
                  {isFirstWalk
                    ? "You are all meeting for your first walk together"
                    : "You are all meeting for your walk together"}
                </h2>

                <h2 className="cb-highlight-blue">
                  {(!selectedBooking.date ||
                    String(selectedBooking.date).trim() === "") &&
                  selectedBooking.availability &&
                  selectedBooking.availability.length > 0
                    ? `with ${selectedBooking.availability.join(", ")}`
                    : `on ${selectedBooking.formatted_date}`}
                </h2>

                <h2 className="cb-highlight-blue">
                  at {selectedBooking.meeting_location}
                </h2>

                {(viewMode === "completed" || hasUserActed) &&
                  !selectedBooking.is_cancelled && (
                    <h2 className="cb-status-text-gray">
                      You have marked this walk as completed
                    </h2>
                  )}

                {selectedBooking.is_cancelled && (
                  <h2 className="cb-status-text-gray" style={{ color: "red" }}>
                    This walk has been cancelled
                  </h2>
                )}
              </div>
            </div>

            <div className="cb-detail-footer">
              {!hasUserActed &&
              viewMode === "default" &&
              !selectedBooking.is_cancelled &&
              selectedBooking.api_status?.toLowerCase() !== "pending" ? (
                <>
                  <button
                    className="cb-action-btn primary"
                    disabled={actionLoading === "Complete"}
                    onClick={() => handleAction("Complete")}
                  >
                    {actionLoading === "Complete" ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      "MARK AS COMPLETED"
                    )}
                  </button>
                  <div className="cb-footer-row">
                    <button
                      className="cb-action-btn secondary"
                      disabled={actionLoading === "Cancel"}
                      onClick={() => handleAction("Cancel")}
                    >
                      {actionLoading === "Cancel" ? (
                        <Loader2 className="animate-spin" size={20} />
                      ) : (
                        "DIDN'T CONNECT"
                      )}
                    </button>
                    <button
                      className="cb-action-btn secondary"
                      onClick={handleRescheduleClick}
                    >
                      RESCHEDULE
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {(viewMode === "completed" || hasUserActed) &&
                    !selectedBooking.is_cancelled &&
                    !isLoggedUserRated && (
                      <button
                        className="cb-action-btn primary"
                        onClick={handleGoToPostWalkFeedbackPage}
                      >
                        RATE
                      </button>
                    )}
                  {!hasUserActed &&
                    viewMode === "default" &&
                    selectedBooking.api_status?.toLowerCase() === "pending" && (
                      <button
                        className="cb-action-btn secondary"
                        style={{ color: "#D32F2F" }}
                        disabled={Boolean(actionLoading)}
                        onClick={() => handleAction("Cancel")}
                      >
                        {actionLoading === "Cancel" ? (
                          <Loader2 className="animate-spin" size={20} />
                        ) : (
                          "CANCEL BOOKING"
                        )}
                      </button>
                    )}
                  <button
                    className="cb-action-btn secondary"
                    onClick={handleGoToMessagesPage}
                  >
                    MESSAGE
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {showCompleteModal && (
        <div className="treat-modal-overlay">
          <div className="treat-modal-card" style={{ height: "300px" }}>
            <button
              className="treat-close-btn"
              onClick={closeAllModalsFully}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>
            <div className="treat-modal-content" style={{ margin: "2rem" }}>
              <h2 className="treat-text-main">
                Congratulations on completing your walk with{" "}
                {selectedBooking?.partnerName}!
              </h2>
            </div>
            <button
              className="cb-action-btn-modal"
              onClick={() => handleAction("NEXT")}
            >
              NEXT STEP
            </button>
          </div>
        </div>
      )}

      {/* BOOK WALK / RESCHEDULE MODAL */}
      {showBookWalkModal && treatDog && (
        <div className="modal-overlay" style={{ zIndex: "9999" }}>
          <div className="book-modal-card">
            <button
              className="modal-close-btn"
              onClick={closeAllModalsFully}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>
            <div className="book-modal-header-icons">
              <div className="pro-treat-dog-avatar">
                <img
                  src={treatDog.displayPartnerAvatar || userPlaceholder}
                  alt={treatDog.displayPartnerName}
                  onError={handleImgError}
                />
              </div>
            </div>

            <h2 className="book-title">When would you like to walk?</h2>
            <p className="book-subtitle">
              <b style={{ color: "#000" }}>Take the first step! 🐾</b>
              <br />
              Invite {getFirstName(
                treatDog?.user_name || treatDog?.dog_name,
              )}{" "}
              for a walk. Suggest a time or keep it flexible, you can work out
              the details together once they accept.
            </p>

             <div className="book-tabs">
              <span
                className={`book-tab ${walkTab === "calendar" ? "active" : ""}`}
                onClick={() => setWalkTab("calendar")}
              >
                Calendar
              </span>
              <span
                className={`book-tab ${walkTab === "flexible" ? "active" : ""}`}
                onClick={() => setWalkTab("flexible")}
              >
                I'm Flexible
              </span>
            </div>

            {walkTab === "calendar" && (
              <div className="tab-content-calendar">
                <div className="calendar-layout">
                  <div className="custom-calendar-container">
                    <div className="cal-header-info"></div>
                    <div className="cal-selected-display">
                      <h2>
                        {value ? format(value, "EEE, MMM d") : "Select a date"}
                      </h2>
                    </div>

                    <div className="rdp-wrapper-purple">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box
                          sx={{ width: "100%", maxWidth: 360 }}
                          onClick={handleCalendarClick}
                        >
                          <DateCalendar
                            value={value}
                            disablePast
                            onChange={(newValue) => {
                              setValue(newValue);
                              setSelectedTime(null);
                              handleScrollToTime();
                            }}
                          />
                        </Box>
                      </LocalizationProvider>
                    </div>
                  </div>

                  <div className="time-selector-area" ref={timeDropdownRef}>
                    <label>TIME</label>

                    <div
                      className="custom-dropdown"
                      style={{
                        backgroundColor: selectedTime
                          ? "#fff"
                          : "rgba(182, 252, 238, 1)",
                      }}
                      onClick={() => setShowTimeDropdown((p) => !p)}
                    >
                      <span>{selectedTime || "Choose a time"}</span>
                      <ChevronDown size={16} />
                    </div>

                    {showTimeDropdown && (
                      <div className="time-dropdown-list">
                        {TIME_SLOTS.map((time) => (
                          <div
                            key={time}
                            className={`time-option ${
                              selectedTime === time ? "active" : ""
                            }`}
                            onClick={() => {
                              setSelectedTime(time);
                              setShowTimeDropdown(false);
                            }}
                          >
                            {time}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="book-modal-next-container">
                      <button
                        className={`book-modal-next-btn ${!selectedTime ? "disabled" : ""}`}
                        disabled={!selectedTime}
                        onClick={() => {
                          const [time, meridian] = selectedTime.split(" ");
                          const [hour, minute] = time.split(":").map(Number);
                          const finalDateTime = value
                            .hour(
                              meridian === "PM" && hour !== 12
                                ? hour + 12
                                : hour === 12 && meridian === "AM"
                                  ? 0
                                  : hour,
                            )
                            .minute(minute)
                            .second(0);
                          setSelectedDate(finalDateTime);
                          openMeetingPoint();
                        }}
                      >
                        NEXT
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {walkTab === "flexible" && (
              <div className="tab-content-flexible">
                <div className="flexible-grid">
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("mornings")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("mornings")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={morningIcon}
                      alt="Mornings"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Mornings</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("afternoons")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("afternoons")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={afternoonIcon}
                      alt="Afternoon"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Afternoons</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("evenings")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("evenings")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={eveningIcon}
                      alt="Evening"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Evenings</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("weekdays")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("weekdays")}
                  >
                    <img
                      src={weekedaysIcon}
                      alt="Weekdays"
                      style={{
                        height: "40px",
                        width: "35px",
                        marginTop: "10px",
                      }}
                    />
                    <span>Weekdays</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("weekends")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("weekends")}
                    style={{ gap: "15px" }}
                  >
                    <img
                      src={weekenedsIcon}
                      alt="Weekends"
                      style={{
                        height: "35px",
                        width: "35px",
                        marginTop: "10px",
                      }}
                    />
                    <span>Weekends</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("vacation")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("vacation")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={vacationIcon}
                      alt="vacations"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Vacations</span>
                  </div>
                </div>
                <div className="book-modal-submit-container">
                  <button
                    className={`book-modal-submit-btn ${selectedFlexibleOptions.length === 0 ? "disabled" : ""}`}
                    disabled={selectedFlexibleOptions.length === 0}
                    onClick={handleFlexibleSubmit}
                  >
                    SUBMIT
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MEETING POINT MODAL */}
      {showMeetingPointModal && (
        <div className="meeting-point-overlay">
          <div className="meeting-point-card">
            <div className="meeting-point-header">
              <button className="mp-back-btn" onClick={backToBookWalk}>
                <ArrowLeft size={22} color="#000" strokeWidth={3} />
                <span style={{ color: "#000", fontWeight: "700" }}>Back</span>
              </button>
              <button
                className="mp-close-btn"
                onClick={closeAllModalsFully}
                style={{ zIndex: 999 }}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>

            <div className="meeting-point-content">
              <h2 className="mp-title">SELECT MEETING LOCATION</h2>
              <p className="mp-subtitle">
                Choose from common locations or tap on map to pin a location on
                map
              </p>

              {/* Custom Dropdown */}
              <MeetingLocationPicker
                profile={profile}
                partnerId={String(selectedBooking?.requester_user_id) === String(profile?.id) ? selectedBooking?.walker_user_id : selectedBooking?.requester_user_id}
                dogId={selectedBooking?.dog_id}
                isMapLoaded={isLoaded}
                initialCenter={userLocation}
                selectedLocation={selectedMeetingLocation}
                selectedAddress={selectedMeetingAddress}
                onSelect={(place) => {
                  setSelectedMeetingLocation(place);
                  setSelectedMeetingAddress(place.address || place.name);
                }}
              />

              <button className="mp-confirm-btn" disabled={!hasMeetingLocation(selectedMeetingAddress)} onClick={openWalkConfirm}>
                NEXT: PERSONALIZE YOUR OFFER
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WALK CONFIRM MODAL (Message) */}
      {showWalkConfirmModal && (
        <div className="walk-confirm-overlay">
          <div className="walk-confirm-card">
            <div className="walk-confirm-header">
              <button className="wc-back-btn" onClick={backToMeetingPoint}>
                <ArrowLeft size={22} color="#000" strokeWidth={3} />
                <span style={{ color: "#000", fontWeight: "700" }}>Back</span>
              </button>
              <button
                className="wc-close-btn"
                onClick={closeAllModalsFully}
                style={{ zIndex: 999 }}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>

            <div className="walk-confirm-content">
              <h2 className="wc-title">
                You would like to walk with {treatDog?.partnerName}{" "}
                {selectedDate ? (
                  `on ${dayjs(selectedDate).format("ddd, MMM D [at] h:mm a")}`
                ) : (
                  <>
                    with <span>{selectedFlexibleOptions.join(", ")}</span>{" "}
                  </>
                )}
              </h2>

              <p className="wc-message-label">
                Personalize your offer with a message
              </p>
              <textarea
                className="wc-message-input"
                placeholder="Write here"
                value={walkMessage}
                onChange={(e) => setWalkMessage(e.target.value)}
              />

              <div className="wc-actions-row">
                <button
                  className="wc-btn confirm"
                  onClick={() => {
                    setShowWalkConfirmModal(false);
                    setShowSuccessModal(true);
                  }}
                >
                  NEXT: CONFIRM YOUR OFFER BEFORE SENDING
                </button>
              </div>
              <p className="wc-footer-text">
                We will reach out to {treatDog?.partnerName} with your suggested
                time and find the best time for your first group walk to either
                confirm or suggest another time or place, check your bookings
                tab to see more!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal-card">
            <button
              className="modal-close-btn"
              onClick={closeAllModalsFully}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>
            <h2 className="success-title">
              Your request to {getFirstName(treatDog?.partnerName)} is sent
            </h2>

            {walkMessage && (
              <div className="success-msg-preview">
                <p style={{ margin: "0 0 4px 0", fontWeight: "600" }}>
                  Your message:
                </p>
                <p style={{ margin: 0, fontStyle: "italic" }}>
                  "{walkMessage}"
                </p>
              </div>
            )}

            <h3 className="success-mp-title">Your Meeting point will be:</h3>
            <div className="success-map-box">
              <div className="success-map-placeholder">
                <div className="group-walk-pill">
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "#448a7b",
                      }}
                    ></div>{" "}
                    GROUP WALK
                  </span>
                </div>
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={selectedMeetingLocation || userLocation}
                    zoom={15}
                    options={{
                      disableDefaultUI: true,
                      gestureHandling: "none",
                    }}
                  >
                    <Marker
                      position={selectedMeetingLocation || userLocation}
                    />
                  </GoogleMap>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#e3f2fd",
                    }}
                  />
                )}
              </div>
              <div className="success-mp-details">
                <h4>
                  {selectedMeetingAddress
                    ? selectedMeetingAddress.split(",")[0]
                    : "Selected Location"}
                </h4>
                <p>
                  <MapPin size={14} color="#009bce" /> {selectedMeetingAddress}
                </p>
              </div>

              <div className="success-chips">
                <div className="success-chip blue">
                  <span>{getDynamicDateLabel(selectedDate)}</span>
                  <strong style={{ color: "#000" }}>
                    {selectedDate
                      ? dayjs(selectedDate).format("ddd, MMM D")
                      : selectedFlexibleOptions.join(", ")}
                  </strong>
                </div>
                {selectedDate && (
                  <div className="success-chip green">
                    <span>{getDynamicTimeLabel(selectedDate)}</span>
                    <strong style={{ color: "#000" }}>
                      {dayjs(selectedDate).format("h:mm A")}
                    </strong>
                  </div>
                )}
              </div>
            </div>
            <button
              className="success-done-btn"
              onClick={handleRescheduleSubmit}
              disabled={isRescheduling}
            >
              {isRescheduling ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Loader2 className="animate-spin" size={18} />
                  <span>DONE</span>
                </div>
              ) : (
                "DONE"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentBookingsPage;
